import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAppIcon, getAppColor, formatCurrency } from "@/lib/utils";
import { License } from "@shared/schema";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { isLicenseExpired } from "@/lib/utils";

interface LicenseUtilizationTableProps {
  includeInactive?: boolean;
}

export default function LicenseUtilizationTable({ includeInactive = false }: LicenseUtilizationTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("utilization");
  const [showInactive, setShowInactive] = useState(includeInactive);
  
  // Update showInactive when filter changes
  useEffect(() => {
    setShowInactive(includeInactive);
  }, [includeInactive]);
  
  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['/api/licenses'],
  });
  
  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ['/api/applications'],
  });

  const isLoading = licensesLoading || appsLoading;
  
  // Filter and sort licenses
  const getFilteredLicenses = () => {
    if (!licenses || !applications || !Array.isArray(licenses) || !Array.isArray(applications)) return [];
    
    return licenses.filter((license: License) => {
      const app = applications.find((app: any) => app.id === license.applicationId);
      const appName = app ? app.name.toLowerCase() : '';
      const licenseName = license.name.toLowerCase();
      
      // Apply search query filter
      const matchesSearch = appName.includes(searchQuery.toLowerCase()) || 
                            licenseName.includes(searchQuery.toLowerCase());
      
      // Apply active/inactive filter — treat expired licenses as inactive
      const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
      const isExpired = !isLifetime && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);
      const isEffectivelyActive = license.status === 'active' && !isExpired;
      const matchesStatus = showInactive || isEffectivelyActive;
      
      return matchesSearch && matchesStatus;
    }).sort((a: License, b: License) => {
      const aUtilization = a.totalSeats > 0 ? (a.assignedSeats / a.totalSeats) : 0;
      const bUtilization = b.totalSeats > 0 ? (b.assignedSeats / b.totalSeats) : 0;
      
      if (sortBy === "utilization") {
        return bUtilization - aUtilization;
      } else if (sortBy === "costPerUser") {
        const aCostPerUser = a.assignedSeats > 0 ? (a.cost / a.assignedSeats) : Infinity;
        const bCostPerUser = b.assignedSeats > 0 ? (b.cost / b.assignedSeats) : Infinity;
        return aCostPerUser - bCostPerUser;
      } else if (sortBy === "totalCost") {
        return b.cost - a.cost;
      } else {
        return b.totalSeats - a.totalSeats;
      }
    });
  };

  const calculateUtilizationClass = (assignedSeats: number, totalSeats: number) => {
    const percentage = (assignedSeats / totalSeats) * 100;
    if (percentage < 50) return "bg-amber-500";
    if (percentage < 80) return "bg-blue-500";
    return "bg-green-600";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg font-semibold">License Utilization</CardTitle>
          <Popover>
            <PopoverTrigger>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <InfoCircledIcon className="h-4 w-4 text-slate-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">About This Table</h4>
                <p className="text-sm text-muted-foreground">
                  This table displays license seat utilization efficiency across your applications.
                  Utilization is calculated as the percentage of purchased seats
                  that are currently assigned to employees.
                </p>
                <h4 className="font-medium text-sm mt-4">Utilization Color Codes</h4>
                <p className="text-sm text-muted-foreground">
                  <span className="inline-block w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                  <strong>Below 50%</strong>: Low utilization, significant savings potential<br />
                  <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                  <strong>50% - 80%</strong>: Moderate utilization, some optimization possible<br />
                  <span className="inline-block w-3 h-3 bg-green-600 rounded-full mr-2"></span>
                  <strong>Above 80%</strong>: Good utilization, optimal license usage
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex space-x-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-slate-400"></i>
            </div>
            <Input
              type="text"
              placeholder="Search..."
              className="pl-10"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <Select defaultValue={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="utilization">Sort by Utilization</SelectItem>
              <SelectItem value="costPerUser">Sort by Cost Per Seat</SelectItem>
              <SelectItem value="totalCost">Sort by Total Cost</SelectItem>
              <SelectItem value="seats">Sort by Total Seats</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application</TableHead>
                  <TableHead>License Type</TableHead>
                  <TableHead>Utilization</TableHead>
                  <TableHead>Cost Per Seat</TableHead>
                  <TableHead>Total Cost</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredLicenses().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No licenses found.
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLicenses().map((license: License) => {
                    const app = applications.find((app: any) => app.id === license.applicationId);
                    const utilizationPercentage = license.totalSeats > 0 ? Math.round((license.assignedSeats / license.totalSeats) * 100) : 0;
                    const costPerSeat = license.assignedSeats > 0 ? license.cost / license.assignedSeats : 0;
                    const isLifetimeLic = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
                    const isExpiredLic = !isLifetimeLic && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);

                    return (
                      <TableRow key={license.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className={`h-10 w-10 rounded ${getAppColor(app?.logo)} flex items-center justify-center`}>
                              <i className={`${getAppIcon(app?.logo)}`}></i>
                            </div>
                            <div>
                              <div className="font-medium">{app?.name}</div>
                              <div className="text-sm text-muted-foreground">{app?.vendor}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{license.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {(license as any).type || license.billingCycle}
                            {isExpiredLic && (
                              <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">
                                Expired
                              </span>
                            )}
                            {!isExpiredLic && license.status !== 'active' && (
                              <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800">
                                Inactive
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{license.assignedSeats}/{license.totalSeats} seats</span>
                              <span>{utilizationPercentage}%</span>
                            </div>
                            <Progress value={utilizationPercentage} className="h-2">
                              <div 
                                className={`h-full rounded-full ${calculateUtilizationClass(license.assignedSeats, license.totalSeats)}`} 
                                style={{ width: `${utilizationPercentage}%` }}
                              ></div>
                            </Progress>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{formatCurrency(costPerSeat)}</div>
                          <div className="text-sm text-muted-foreground">{license.billingCycle}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{formatCurrency(license.cost)}</div>
                          <div className="text-sm text-muted-foreground">{license.billingCycle}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm">
                            Optimize
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
